import React, { useState, useEffect } from "react";
import { Flex, Heading, Form, Button } from "@nubeteck/components";
import {
  useGetGeneralSettingsMutation,
  useResetGeneralSettingsMutation,
  useSaveGeneralSettingsMutation,
} from "src/services/generalSettingsServices";
import ConfigurationInput from "./settingsInput";
import GeneralTemplatesTranslation from "./general-templates-translation";
import { Collapse, CollapseProps, Spin, Skeleton } from "antd";
import {
  useErrorSavingDataToast,
  useSuccessSettingsUpdatedToast,
  useSuccessRegisterToast,
} from "src/constants";
import { ISettingsSection } from "src/Interfaces/generalSettings";
import { themeColors } from "src/core";
import { getFormData } from "src/utils/files-util";
// import settingsImg from "src/assets/Settings.svg";
import { getSettingsPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";

const GeneralSettingsView = () => {
  const { locale } = useLocale();
  const [settingsPage, setSettingsPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const [dynamicLabels, setDynamicLabels] = useState<{[key: string]: string}>({});
  const [showTemplatesTranslation, setShowTemplatesTranslation] = useState(false);
  const showSuccessToast = useSuccessRegisterToast();
  const showErrorSavingToast = useErrorSavingDataToast();
  const showSuccessSettingsUpdatedToast = useSuccessSettingsUpdatedToast();

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSettingsPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getSettingsPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const [getSettings, { data: configuraciones, isLoading: isLoadingSettings }] =
    useGetGeneralSettingsMutation();
  const [
    saveSettings,
    { isLoading: isLoadingSaving, isSuccess: isSuccessSaving, isError: isErrorSaving },
  ] = useSaveGeneralSettingsMutation();
  const [
    resetSettings,
    { isLoading: isLoadingDefault, isSuccess: isSuccessDefault, isError: isErrorDefault },
  ] = useResetGeneralSettingsMutation();

  const [form] = Form.useForm<any>();

  React.useEffect(() => {
    if (!isLoadingContent) {
      // Usar el endpoint original GeneralSettings para obtener todos los campos
      getSettings();
    }
  }, [getSettings, isLoadingContent]);

  // Inicializar valores del formulario una sola vez cuando se cargan las configuraciones
  React.useEffect(() => {
    if (configuraciones?.data && !isLoadingSettings) {
      const sections = Array.isArray(configuraciones.data) 
        ? configuraciones.data 
        : [];
      
      const initialValues: {[key: string]: any} = {};
      sections.forEach((section: ISettingsSection) => {
        if (section?.configuraciones && Array.isArray(section.configuraciones)) {
          section.configuraciones.forEach((field) => {
            initialValues[field.nombre] = field.valor;
          });
        }
      });
      
      form.setFieldsValue(initialValues);
    }
  }, [configuraciones?.data, isLoadingSettings, form]);

  // useEffect para manejar el cambio de idioma y actualizar los labels dinámicamente
  useEffect(() => {
    if (configuraciones?.data) {
      const newDynamicLabels: {[key: string]: string} = {};
      
      // Verificar si data es un array (estructura antigua) o un objeto (estructura nueva)
      const sections = Array.isArray(configuraciones.data) 
        ? configuraciones.data 
        : [];
      
      sections.forEach((section: ISettingsSection) => {
        if (section?.configuraciones && Array.isArray(section.configuraciones)) {
          section.configuraciones.forEach((field) => {
            // Usar field.clave como clave para el mapeo
            const key = field.clave || field.label;
            // Si el idioma es inglés, usar field.clave, si no usar field.label
            newDynamicLabels[key] = locale === 'en' && field.clave ? field.clave : field.label;
          });
        }
      });
      
      setDynamicLabels(newDynamicLabels);
    }
  }, [locale, configuraciones?.data]);

  React.useEffect(() => {
    if (isErrorSaving) showErrorSavingToast();

    if (isSuccessSaving) {
      showSuccessSettingsUpdatedToast();
      getSettings();
    }
  }, [isSuccessSaving, form, isErrorSaving, getSettings, showErrorSavingToast, showSuccessSettingsUpdatedToast]);

  React.useEffect(() => {
    if (isErrorDefault) showErrorSavingToast();

    if (isSuccessDefault) {
      getSettings();
      showSuccessToast();
    }
  }, [isSuccessDefault, form, isErrorDefault, getSettings, showSuccessToast, showErrorSavingToast]);

  const submit = (data: any) => {
    saveSettings(getFormData(data));
  };

  const items: CollapseProps["items"] = React.useMemo(() => {
    if (isLoadingContent || !configuraciones?.data) {
      return undefined;
    }

    // Verificar si data es un array (estructura esperada)
    const sections = Array.isArray(configuraciones.data) 
      ? configuraciones.data 
      : [];

    if (sections.length === 0) {
      return undefined;
    }

    return sections.map((item: ISettingsSection) => ({
      key: item.id,
      label: settingsPage?.system_setting || "Configuraciones del sistema",
      children: item?.configuraciones?.map((field) => {
        return (
          <div key={field.id}>
            <Form.Item
              rules={[{ required: field.requerido }]}
              label={dynamicLabels[field.clave ?? field.label]}
              name={field.nombre}
            >
              <ConfigurationInput
                data={field}
                onChange={(value) => form.setFieldValue(field.nombre, value)}
                disabled={false}
                readOnly={false}
              />
            </Form.Item>
            <Button
              type="text"
              onClick={() => resetSettings(field.nombre)}
              style={{ color: themeColors.colorAccent, fontWeight: "bold" }}
            >
              {settingsPage?.reset_opt || "Reestablecer a su valor por defecto"}
            </Button>
            <br />
            <br />
          </div>
        );
      }),
    }));
  }, [configuraciones?.data, form, resetSettings, settingsPage, isLoadingContent, dynamicLabels]);

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setSettingsPage(null);
      }}
    />;
  }

  return (
    <Flex style={{ width: "100%", flexDirection: "column", maxWidth: "1440px" }}>
      {isLoadingContent ? (
        <>
          <Skeleton.Input active size="large" style={{ width: 300, marginBottom: 16 }} />
          <Skeleton active paragraph={{ rows: 8 }} />
          <Skeleton.Input active size="default" style={{ width: 100, alignSelf: "flex-end", marginTop: 16 }} />
        </>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={submit}
          noValidate
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Heading level={4}>{settingsPage?.generalsetting_nb || "Configuraciones generales"}</Heading>
            <Button type="primary" onClick={() => setShowTemplatesTranslation(!showTemplatesTranslation)}>{settingsPage?.translate_template?.translate_button || "Traducciones"}</Button>
          </div>
          {showTemplatesTranslation ? (
            <GeneralTemplatesTranslation onBack={() => setShowTemplatesTranslation(false)} />
          ) : (
            <>
              <Spin spinning={isLoadingSettings || isLoadingSaving || isLoadingDefault} size="large">
                {!isLoadingContent && configuraciones?.data && (
                  <Collapse
                    bordered={false}
                    items={items}
                    defaultActiveKey={["1"]}
                    style={{ fontSize: "20px", fontWeight: "bold" }}
                  />
                )}
                <br />
                {!isLoadingContent && (
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    style={{ alignSelf: "flex-end" }}
                    loading={isLoadingSaving}
                  >
                    {settingsPage?.save_button || "Guardar"}
                  </Button>
                )}
              </Spin>
            </>
          )}
        </Form>
      )}
    </Flex>
  );
};

export default GeneralSettingsView;
